import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, ShoppingCart, Tag, Layers, BarChart3, Settings, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';
import { getAuthHeaders } from '../../hooks/useAdmin';
import { formatCurrency, formatDateTimeShort } from '../../utils/format';
import { ShopAnalyticsView } from './shop/ShopAnalyticsView';
import { ShopCustomersView } from './shop/ShopCustomersView';
import { ShopSettingsView } from './shop/ShopSettingsView';
import { ShopDiscountsView } from './shop/ShopDiscountsView';
import { ShopDiscountDialog } from './shop/ShopDiscountDialog';
import { ShopOrdersView } from './shop/ShopOrdersView';
import { ShopOrderDetailDialog } from './shop/ShopOrderDetailDialog';
import { ShopShippingDialog } from './shop/ShopShippingDialog';
import { ShopProductsView } from './shop/ShopProductsView';
import { ShopProductDialog } from './shop/ShopProductDialog';
import { ShopDeleteProductDialog } from './shop/ShopDeleteProductDialog';


export function AdminShopTab() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [activeView, setActiveView] = useState('products'); // products, orders, analytics, settings
  
  // Shop settings state
  const [shopSettings, setShopSettings] = useState({
    shop_name: 'Love 2 Line Dance',
    tagline: 'Quality merchandise for line dance enthusiasts',
    logo_url: null
  });
  const [settingsForm, setSettingsForm] = useState({
    shop_name: '',
    tagline: '',
    logo_url: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Product dialog state
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    compare_at_price: '',
    sku: '',
    stock_quantity: 0,
    track_inventory: true,
    allow_backorder: true,
    is_discontinued: false,
    is_active: true,
    images: [],
    tags: [],
    option_types: [],
    variants: []
  });
  const [saving, setSaving] = useState(false);
  
  // Variant builder state
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [variantPrices, setVariantPrices] = useState({});
  const [variantStocks, setVariantStocks] = useState({});
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  
  // Order detail dialog
  const [orderDialog, setOrderDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Shipping dialog state
  const [shippingDialog, setShippingDialog] = useState(false);
  const [shippingForm, setShippingForm] = useState({
    trackingNumber: '',
    trackingUrl: ''
  });
  const [shippingLoading, setShippingLoading] = useState(false);
  
  // Image upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Discount codes state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountDialog, setDiscountDialog] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    expires_at: '',
    is_active: true
  });
  const [savingDiscount, setSavingDiscount] = useState(false);

  // Customer accounts state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchOrders();
    fetchAnalytics();
    fetchShopSettings();
    fetchDiscountCodes();
  }, []);

  const fetchShopSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/settings`);
      if (res.ok) {
        const data = await res.json();
        setShopSettings(data);
        setSettingsForm({
          shop_name: data.shop_name || '',
          tagline: data.tagline || '',
          logo_url: data.logo_url || ''
        });
      }
    } catch (error) {
      console.error('Error fetching shop settings:', error);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/discount-codes`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setDiscountCodes(data.discount_codes || []);
      }
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    }
  };

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/customers`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleCreateDiscount = async () => {
    if (!discountForm.code || !discountForm.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }

    setSavingDiscount(true);
    try {
      const payload = {
        code: discountForm.code.toUpperCase(),
        discount_type: discountForm.discount_type,
        discount_value: parseFloat(discountForm.discount_value),
        min_order_amount: discountForm.min_order_amount ? parseFloat(discountForm.min_order_amount) : null,
        max_uses: discountForm.max_uses ? parseInt(discountForm.max_uses) : null,
        expires_at: discountForm.expires_at || null,
        is_active: discountForm.is_active
      };

      const res = await fetch(`${API_URL}/api/shop/admin/discount-codes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Discount code created');
        setDiscountDialog(false);
        setDiscountForm({
          code: '', discount_type: 'percentage', discount_value: '', 
          min_order_amount: '', max_uses: '', expires_at: '', is_active: true
        });
        fetchDiscountCodes();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create discount code');
      }
    } catch (error) {
      console.error('Create discount error:', error);
      toast.error('Failed to create discount code');
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleDeleteDiscount = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/discount-codes/${code}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Discount code deleted');
        fetchDiscountCodes();
      } else {
        toast.error('Failed to delete discount code');
      }
    } catch (error) {
      toast.error('Failed to delete discount code');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setSettingsForm(prev => ({ ...prev, logo_url: data.url }));
        toast.success('Logo uploaded');
      } else {
        toast.error('Failed to upload logo');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settingsForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setShopSettings(data);
        toast.success('Shop settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/products?limit=100`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/orders?limit=100`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/analytics?days=30`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductForm({
      title: '',
      description: '',
      category: '',
      price: '',
      compare_at_price: '',
      sku: '',
      stock_quantity: 0,
      track_inventory: true,
      allow_backorder: true,
      is_discontinued: false,
      is_active: true,
      images: [],
      tags: [],
      option_types: [],
      variants: []
    });
    setSelectedSizes([]);
    setSelectedColors([]);
    setVariantPrices({});
    setVariantStocks({});
    setProductDialog(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title || '',
      description: product.description || '',
      category: product.category || '',
      price: product.price?.toString() || '',
      compare_at_price: product.compare_at_price?.toString() || '',
      sku: product.sku || '',
      stock_quantity: product.stock_quantity || 0,
      track_inventory: product.track_inventory !== false,
      allow_backorder: product.allow_backorder !== false,
      is_discontinued: product.is_discontinued || false,
      is_active: product.is_active !== false,
      images: product.images || [],
      tags: product.tags || [],
      option_types: product.option_types || [],
      variants: product.variants || []
    });
    
    // Restore variant selections
    if (product.variants?.length > 0) {
      const sizes = new Set();
      const colors = new Set();
      const prices = {};
      const stocks = {};
      
      product.variants.forEach(v => {
        if (v.options?.size) sizes.add(v.options.size);
        if (v.options?.color) colors.add(v.options.color);
        const key = `${v.options?.size || ''}-${v.options?.color || ''}`;
        prices[key] = v.price;
        stocks[key] = v.stock_quantity;
      });
      
      setSelectedSizes([...sizes]);
      setSelectedColors([...colors]);
      setVariantPrices(prices);
      setVariantStocks(stocks);
    } else {
      setSelectedSizes([]);
      setSelectedColors([]);
      setVariantPrices({});
      setVariantStocks({});
    }
    
    setProductDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setProductForm(prev => ({
          ...prev,
          images: [...prev.images, data.url]
        }));
        toast.success('Image uploaded');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const generateVariants = () => {
    if (selectedSizes.length === 0 && selectedColors.length === 0) {
      return [];
    }
    
    const variants = [];
    const basePrice = parseFloat(productForm.price) || 0;
    
    if (selectedSizes.length > 0 && selectedColors.length > 0) {
      // Generate Size/Color combinations
      for (const size of selectedSizes) {
        for (const color of selectedColors) {
          const key = `${size}-${color}`;
          variants.push({
            name: `${size} / ${color}`,
            options: { size, color },
            price: variantPrices[key] || basePrice,
            stock_quantity: variantStocks[key] || 0,
            track_inventory: true,
            is_active: true
          });
        }
      }
    } else if (selectedSizes.length > 0) {
      // Size only
      for (const size of selectedSizes) {
        const key = `${size}-`;
        variants.push({
          name: size,
          options: { size },
          price: variantPrices[key] || basePrice,
          stock_quantity: variantStocks[key] || 0,
          track_inventory: true,
          is_active: true
        });
      }
    } else if (selectedColors.length > 0) {
      // Color only
      for (const color of selectedColors) {
        const key = `-${color}`;
        variants.push({
          name: color,
          options: { color },
          price: variantPrices[key] || basePrice,
          stock_quantity: variantStocks[key] || 0,
          track_inventory: true,
          is_active: true
        });
      }
    }
    
    return variants;
  };

  const handleSaveProduct = async () => {
    if (!productForm.title.trim()) {
      toast.error('Product title is required');
      return;
    }
    if (!productForm.price || parseFloat(productForm.price) <= 0) {
      toast.error('Valid price is required');
      return;
    }
    
    setSaving(true);
    
    const variants = generateVariants();
    const optionTypes = [];
    if (selectedSizes.length > 0) optionTypes.push('Size');
    if (selectedColors.length > 0) optionTypes.push('Color');
    
    const payload = {
      title: productForm.title.trim(),
      description: productForm.description.trim(),
      product_type: 'physical',
      category: productForm.category || null,
      tags: productForm.tags,
      images: productForm.images,
      price: parseFloat(productForm.price),
      compare_at_price: productForm.compare_at_price ? parseFloat(productForm.compare_at_price) : null,
      stock_quantity: parseInt(productForm.stock_quantity) || 0,
      track_inventory: productForm.track_inventory,
      allow_backorder: productForm.allow_backorder,
      is_discontinued: productForm.is_discontinued,
      sku: productForm.sku || null,
      is_active: productForm.is_active,
      option_types: optionTypes,
      variants: variants
    };
    
    try {
      const url = editingProduct 
        ? `${API_URL}/api/shop/admin/products/${editingProduct.product_id}`
        : `${API_URL}/api/shop/admin/products`;
      
      const res = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(editingProduct ? 'Product updated' : 'Product created');
        setProductDialog(false);
        fetchProducts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/products/${productToDelete.product_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Product deleted');
        setDeleteDialog(false);
        setProductToDelete(null);
        fetchProducts();
      } else {
        toast.error('Failed to delete product');
      }
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/products/${product.product_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...product,
          is_active: !product.is_active
        })
      });
      
      if (res.ok) {
        toast.success(product.is_active ? 'Product hidden' : 'Product visible');
        fetchProducts();
      }
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status, trackingNumber = null, trackingUrl = null) => {
    if (updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const body = { status };
      if (trackingNumber) body.tracking_number = trackingNumber;
      if (trackingUrl) body.tracking_url = trackingUrl;
      
      const res = await fetch(`${API_URL}/api/shop/admin/orders/${orderId}/fulfillment`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        const STATUS_LABELS = { pending: 'Pending', processing: 'Processing', shipped: 'Shipped', delivered: 'Delivered', ready_for_collection: 'Ready for Collection', collected: 'Collected', cancelled: 'Cancelled' };
        toast.success(`Order marked as ${STATUS_LABELS[status] || status}`);
        fetchOrders();
        if (selectedOrder?.order_id === orderId) {
          setSelectedOrder(prev => ({ 
            ...prev, 
            fulfillment_status: status,
            tracking_number: trackingNumber || prev.tracking_number,
            tracking_url: trackingUrl || prev.tracking_url
          }));
        }
      } else {
        let message = 'Failed to update order';
        try {
          const err = await res.json();
          if (err?.detail) message = typeof err.detail === 'string' ? err.detail : message;
        } catch (e) { /* non-JSON response */ }
        toast.error(message);
      }
    } catch (error) {
      toast.error('Failed to update order');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkShipped = () => {
    setShippingForm({ trackingNumber: '', trackingUrl: '' });
    setShippingDialog(true);
  };

  const handleConfirmShipping = async () => {
    if (!selectedOrder) return;
    
    setShippingLoading(true);
    await handleUpdateOrderStatus(
      selectedOrder.order_id, 
      'shipped', 
      shippingForm.trackingNumber || null,
      shippingForm.trackingUrl || null
    );
    setShippingLoading(false);
    setShippingDialog(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Shop Management</h2>
          <p className="text-slate-600">Manage products, orders, and inventory</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeView === 'products' ? 'default' : 'outline'}
            onClick={() => setActiveView('products')}
            size="sm"
          >
            <Package className="h-4 w-4 mr-2" />
            Products
          </Button>
          <Button
            variant={activeView === 'orders' ? 'default' : 'outline'}
            onClick={() => setActiveView('orders')}
            size="sm"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Orders
            {orders.filter(o => o.fulfillment_status === 'pending').length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {orders.filter(o => o.fulfillment_status === 'pending').length}
              </Badge>
            )}
          </Button>
          <Button
            variant={activeView === 'analytics' ? 'default' : 'outline'}
            onClick={() => setActiveView('analytics')}
            size="sm"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </Button>
          <Button
            variant={activeView === 'discounts' ? 'default' : 'outline'}
            onClick={() => setActiveView('discounts')}
            size="sm"
          >
            <Tag className="h-4 w-4 mr-2" />
            Discounts
          </Button>
          <Button
            variant={activeView === 'customers' ? 'default' : 'outline'}
            onClick={() => { setActiveView('customers'); fetchCustomers(); }}
            size="sm"
          >
            <Layers className="h-4 w-4 mr-2" />
            Customers
          </Button>
          <Button
            variant={activeView === 'settings' ? 'default' : 'outline'}
            onClick={() => setActiveView('settings')}
            size="sm"
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Products View */}
      {activeView === 'products' && (
        <ShopProductsView
          products={products}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          categoryFilter={categoryFilter}
          onCategoryChange={setCategoryFilter}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          onAddProduct={handleAddProduct}
          onEditProduct={handleEditProduct}
          onToggleActive={handleToggleActive}
          onDeleteProduct={(product) => {
            setProductToDelete(product);
            setDeleteDialog(true);
          }}
          apiUrl={API_URL}
        />
      )}

      {/* Orders View */}
      {activeView === 'orders' && (
        <ShopOrdersView
          orders={orders}
          onViewOrder={(order) => {
            setSelectedOrder(order);
            setOrderDialog(true);
          }}
        />
      )}

      {/* Analytics View */}
      {activeView === 'analytics' && (
        <ShopAnalyticsView analytics={analytics} />
      )}

      {/* Discounts View */}
      {activeView === 'discounts' && (
        <ShopDiscountsView
          discountCodes={discountCodes}
          onCreateClick={() => setDiscountDialog(true)}
          onDeleteDiscount={handleDeleteDiscount}
        />
      )}

      {/* Customers View */}
      {activeView === 'customers' && (
        <ShopCustomersView customers={customers} loading={customersLoading} />
      )}

      {/* Settings View */}
      {activeView === 'settings' && (
        <ShopSettingsView
          settingsForm={settingsForm}
          onSettingsFormChange={setSettingsForm}
          apiUrl={API_URL}
          logoInputRef={logoInputRef}
          uploadingLogo={uploadingLogo}
          savingSettings={savingSettings}
          onLogoUpload={handleLogoUpload}
          onSaveSettings={handleSaveSettings}
        />
      )}

      {/* Product Dialog */}
      <ShopProductDialog
        open={productDialog}
        onOpenChange={setProductDialog}
        editingProduct={editingProduct}
        productForm={productForm}
        onFormChange={setProductForm}
        selectedSizes={selectedSizes}
        onSizesChange={setSelectedSizes}
        selectedColors={selectedColors}
        onColorsChange={setSelectedColors}
        variantPrices={variantPrices}
        onVariantPricesChange={setVariantPrices}
        variantStocks={variantStocks}
        onVariantStocksChange={setVariantStocks}
        uploading={uploading}
        onImageUpload={handleImageUpload}
        onRemoveImage={removeImage}
        saving={saving}
        onSave={handleSaveProduct}
        apiUrl={API_URL}
        fileInputRef={fileInputRef}
      />

      {/* Delete Confirmation Dialog */}
      <ShopDeleteProductDialog
        open={deleteDialog}
        onOpenChange={setDeleteDialog}
        product={productToDelete}
        onConfirm={handleDeleteProduct}
      />

      {/* Order Detail Dialog */}
      <ShopOrderDetailDialog
        open={orderDialog}
        onOpenChange={setOrderDialog}
        order={selectedOrder}
        onUpdateStatus={handleUpdateOrderStatus}
        onMarkShipped={handleMarkShipped}
        updating={updatingStatus}
      />

      {/* Shipping Dialog */}
      <ShopShippingDialog
        open={shippingDialog}
        onOpenChange={setShippingDialog}
        shippingForm={shippingForm}
        onFormChange={setShippingForm}
        onConfirm={handleConfirmShipping}
        loading={shippingLoading}
      />

      {/* Discount Code Dialog */}
      <ShopDiscountDialog
        open={discountDialog}
        onOpenChange={setDiscountDialog}
        discountForm={discountForm}
        onFormChange={setDiscountForm}
        onSubmit={handleCreateDiscount}
        saving={savingDiscount}
      />
    </div>
  );
}

export default AdminShopTab;
